import { Map, fromJS } from 'immutable';
import { isMobile } from '../components/PicabooResponsive';
import logEvent from '../utils/TagManager';
import { z } from 'zod';

const initialState = {
  showmodal: false,
  editTextModalOpen: false,
  nextModal: null,
};

/**
 *  action constants
 */
export const SHOW_MODAL = 'modals/SHOW_MODAL';
export const SHOW_GENERIC_ERROR_MODAL = 'modals/SHOW_GENERIC_ERROR_MODAL';
export const SHOW_MODAL_NO_BODY = 'modals/SHOW_MODAL_NO_BODY';
export const SHOW_LEFTOVER_MODAL = 'modals/SHOW_LEFTOVER_MODAL';
export const SHOW_PROJECT_NAME_MODAL = 'SHOW_PROJECT_NAME_MODAL';
export const SHOW_COPY_PROJECT_NAME_MODAL = 'SHOW_COPY_PROJECT_NAME_MODAL';
export const EDIT_TEXT_MODAL_OPEN = 'EDIT_TEXT_MODAL_OPEN';
export const HIDE_MODAL = 'modals/HIDE_MODAL';
export const SHOW_PICABOOLIGHTBOX = 'modals/SHOW_PICABOOLIGHTBOX';
export const SHOW_PROCESSING = 'modals/SHOW_PROCESSING';
export const SHOW_WAITING = 'modals/SHOW_WAITING';
export const SHOW_LOGIN_MODAL = 'modals/SHOW_LOGIN_MODAL';
export const SHOW_PREFERENCES_MODAL = 'modals/SHOW_PREFERENCES_MODAL';
export const SHOW_LAYOUT_ADJUST_MODAL = 'modals/SHOW_LAYOUT_ADJUST_MODAL';
export const SHOW_CONFIRMATION_MODAL = 'modals/SHOW_CONFIRMATION_MODAL';
export const SHOW_SUCCESS_MODAL = 'modals/SHOW_SUCCESS_MODAL';
export const SHOW_AUTO_ADJUST_LAYOUT_MODAL =
  'modals/SHOW_AUTO_ADJUST_LAYOUT_MODAL';
export const SHOW_PRE_RESIZE_FAILED = 'modal/SHOW_PRE_RESIZE_FAILED';
export const SHOW_IMAGE_UPLOAD_FAILED = 'modal/SHOW_IMAGE_UPLOAD_FAILED';
export const SHOW_SHARE_PROJECT_MODAL = 'modal/SHOW_SHARE_PROJECT_MODAL';
export const SHOW_LEAVE_PROJECT_MODAL = 'modal/SHOW_LEAVE_PROJECT_MODAL';
export const SHOW_ADD_TO_CART_MODAL = 'modal/SHOW_ADD_TO_CARD_MODAL';
export const SHOW_SAVE_LAYOUT_MODAL = 'modal/SAVE_LAYOUT_MODAL';
export const SHOW_DELETE_SAVED_LAYOUT_MODAL =
  'modal/SHOW_DELETE_SAVED_LAYOUT_MODAL';

/**
 *  reducers
 */
const reducer = function modals(state = new Map(initialState), action) {
  switch (action.type) {
    case EDIT_TEXT_MODAL_OPEN:
      return state.set('editTextModalOpen', action.value);
    case HIDE_MODAL:
      if (state.get('nextModal')) {
        return state.get('nextModal');
      } else if (action.hard) {
        // Remove the modal entirely (breaks close modal animation)
        return fromJS({ ...initialState });
      } else {
        return state.set('showmodal', false);
      }
    case SHOW_LOGIN_MODAL:
    case SHOW_GENERIC_ERROR_MODAL:
    case SHOW_SHARE_PROJECT_MODAL:
    case SHOW_LEAVE_PROJECT_MODAL:
    case SHOW_LEFTOVER_MODAL:
    case SHOW_PROJECT_NAME_MODAL:
    case SHOW_COPY_PROJECT_NAME_MODAL:
    case SHOW_MODAL:
    case SHOW_MODAL_NO_BODY:
    case SHOW_PICABOOLIGHTBOX:
    case SHOW_PROCESSING:
    case SHOW_WAITING:
    case SHOW_PREFERENCES_MODAL:
    case SHOW_LAYOUT_ADJUST_MODAL:
    case SHOW_CONFIRMATION_MODAL:
    case SHOW_SUCCESS_MODAL:
    case SHOW_PRE_RESIZE_FAILED:
    case SHOW_IMAGE_UPLOAD_FAILED:
    case SHOW_ADD_TO_CART_MODAL:
    case SHOW_SAVE_LAYOUT_MODAL:
    case SHOW_DELETE_SAVED_LAYOUT_MODAL:
      return fromJS(action);
  }

  return state;
};

export function showModal(title, text) {
  return {
    type: SHOW_MODAL,
    showmodal: true,
    editTextModalOpen: false,
    title: title,
    text: text,
  };
}

export function showModalWithTranslation(titleKey, textKey, hasSupportLink) {
  return {
    type: SHOW_MODAL,
    showmodal: true,
    editTextModalOpen: false,
    titleKey,
    textKey,
    hasSupportLink,
  };
}

export function showWaiting(title, text) {
  return {
    type: SHOW_WAITING,
    showmodal: true,
    editTextModalOpen: false,
    title: title,
    text: text,
  };
}

export function showLeftoverCaptionsModal(leftovers) {
  logEvent('Modals', 'Leftover text', leftovers, 0);
  return {
    type: SHOW_LEFTOVER_MODAL,
    showmodal: true,
    editTextModalOpen: false,
    title: 'Some of your text was removed from the page',
    text: isMobile()
      ? 'You can copy the text below and paste it into a new text box.'
      : 'You can copy the text below and paste it into a new text box using Ctrl + C (PC) or Cmd + C (Mac).',
    leftovers: leftovers,
  };
}

export function showProjectNameModal() {
  return {
    type: SHOW_PROJECT_NAME_MODAL,
    showmodal: true,
    editTextModalOpen: false,
  };
}

export function showShareProjectModal(shareID) {
  return {
    type: SHOW_SHARE_PROJECT_MODAL,
    shareID,
    editTextModalOpen: false,
    showmodal: true,
  };
}

export function showLeaveProjectModal() {
  return {
    type: SHOW_LEAVE_PROJECT_MODAL,
    editTextModalOpen: false,
    showmodal: true,
  };
}

export function showCopyProjectModal() {
  return {
    type: SHOW_COPY_PROJECT_NAME_MODAL,
    showmodal: true,
    editTextModalOpen: false,
  };
}

export function hideModal({ hard = true } = {}) {
  return {
    type: HIDE_MODAL,
    hard,
  };
}

export function editTextModalOpen(value) {
  return {
    type: EDIT_TEXT_MODAL_OPEN,
    value: value,
  };
}

export function showPicaboolightbox(photo, filename, datetaken, orientation) {
  return {
    type: SHOW_PICABOOLIGHTBOX,
    showmodal: true,
    photo: photo, //url
    filename: filename,
    datetaken: datetaken,
    orientation: orientation,
  };
}

export function showProcessing() {
  return {
    type: SHOW_PROCESSING,
    showmodal: true,
  };
}

export const LoginModalConfig = z.strictObject({
  addToCart: z.boolean(),
  reload: z.boolean(),
});

/**
 * Show login modal, and then optionally add to cart or reload the page.
 * @param config
 * @returns {{showmodal: boolean, type: string, config: {reload?: ZodBoolean["_output"], addToCart?: ZodBoolean["_output"]}}}
 */
export function showLoginModal(config = { addToCart: false, reload: false }) {
  return {
    type: SHOW_LOGIN_MODAL,
    showmodal: true,
    config: LoginModalConfig.parse(config),
  };
}

export function showPreferencesModal() {
  return {
    type: SHOW_PREFERENCES_MODAL,
    showmodal: true,
  };
}

export function showLayoutAdjustModal(response) {
  return {
    type: SHOW_LAYOUT_ADJUST_MODAL,
    showmodal: true,
    response,
  };
}

export function showConfirmationModal(
  response,
  title,
  text,
  subtext = null,
  yesText = 'Yes',
  noText = 'No',
) {
  return {
    type: SHOW_CONFIRMATION_MODAL,
    showmodal: true,
    response,
    title,
    text,
    subtext,
    yesText,
    noText,
  };
}

/**
 * Shows a simple success modal with a title, optional body text, and a single button.
 * @param titleKey Translation key for the title.
 * @param textKey (optional) Translation key for the body text.
 * @param buttonKey (optional) Translation key for the button text.
 * @returns {{type: string, showmodal: boolean}}
 */
export function showSuccessModal(
  titleKey,
  textKey = '',
  buttonKey = 'MODAL.Continue',
) {
  return {
    type: SHOW_SUCCESS_MODAL,
    showmodal: true,
    titleKey,
    textKey,
    buttonKey,
  };
}

export function showAddToCartModal() {
  return {
    type: SHOW_ADD_TO_CART_MODAL,
    showmodal: true,
  };
}

export function showAddToCartModalSelector(state) {
  return state.modals.get('type') === SHOW_ADD_TO_CART_MODAL;
}

export function showLoginModalSelector(state) {
  return state.modals.get('type') === SHOW_LOGIN_MODAL;
}

export function showGenericErrorModal() {
  return {
    type: SHOW_GENERIC_ERROR_MODAL,
    showmodal: true,
    editTextModalOpen: false,
  };
}

export function showSaveLayoutModal(pageId) {
  return {
    type: SHOW_SAVE_LAYOUT_MODAL,
    showmodal: true,
    editTextModalOpen: false,
    pageId,
  };
}

export function saveLayoutModalPageIdSelector(state) {
  return state.modals.get('pageId');
}

export function showDeleteSavedLayoutModal(layoutId) {
  return {
    type: SHOW_DELETE_SAVED_LAYOUT_MODAL,
    showmodal: true,
    editTextModalOpen: false,
    layoutId,
  };
}

export function deleteSavedLayoutModalLayoutIdSelector(state) {
  return state.modals.get('layoutId');
}

/** ***********************************************************************
 *  Main Export
 *  *********************************************************************** */
export default {
  reducer: reducer,
};
